// MCHD Room Scheduler - Create Floor Modal
// Modal component for creating a single floor with real-time availability check
// Created: 09-10-2025 13:26:21

import { Modal, Stack, TextInput, NumberInput, Button, Alert, Text, Group, Loader } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconAlertCircle, IconBuilding } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { useCreateFloor, useCheckFloorAvailability } from '../../hooks/admin/useFloors';

// =============================================================================
// TYPES
// =============================================================================

interface CreateFloorModalProps {
  opened: boolean;
  onClose: () => void;
  buildingId: string;
  buildingName: string;
  existingFloorNumbers: number[];
  onSuccess: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * CreateFloorModal Component
 * 
 * Modal for creating a single floor with:
 * - Real-time availability check (500ms debounce)
 * - Auto-suggest floor name based on number
 * - Conflict detection with actionable suggestions
 * - Hospital-grade validation (1-999 range, 2-100 chars)
 * 
 * Created: 09-10-2025 13:26:21
 * @param props - Component props
 * @returns Create floor modal
 */
export function CreateFloorModal({
  opened,
  onClose,
  buildingId,
  buildingName,
  existingFloorNumbers,
  onSuccess
}: CreateFloorModalProps) {
  // =============================================================================
  // STATE
  // =============================================================================
  
  const [debouncedNumber, setDebouncedNumber] = useState<number | null>(null);
  
  // =============================================================================
  // FORM SETUP
  // =============================================================================
  
  const form = useForm({
    initialValues: {
      number: '',
      name: ''
    },
    validate: {
      number: (value) => {
        const num = parseInt(value);
        if (!value) return 'Floor number is required';
        if (isNaN(num)) return 'Floor number must be a number';
        if (num < 1) return 'Floor numbers start at 1';
        if (num > 999) return 'Floor numbers cannot exceed 999';
        if (existingFloorNumbers.includes(num)) return `Floor ${num} already exists`;
        return null;
      },
      name: (value) => {
        if (!value?.trim()) return 'Floor name is required';
        if (value.trim().length < 2) return 'Floor name must be at least 2 characters';
        if (value.length > 100) return 'Floor name cannot exceed 100 characters';
        return null;
      }
    }
  });
  
  // =============================================================================
  // DEBOUNCE EFFECT
  // =============================================================================
  
  /**
   * Debounce floor number for availability check (500ms)
   * Prevents excessive API calls while user is typing
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      const num = parseInt(form.values.number);
      if (!isNaN(num) && num > 0 && num < 1000) {
        setDebouncedNumber(num);
      } else {
        setDebouncedNumber(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.values.number]);
  
  // =============================================================================
  // AUTO-SUGGEST NAME
  // =============================================================================
  
  /**
   * Auto-suggest floor name based on number
   * Only triggers if name field is empty
   */
  useEffect(() => {
    if (form.values.number && !form.values.name) {
      form.setFieldValue('name', `Floor ${form.values.number}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.number]);
  
  // =============================================================================
  // AVAILABILITY CHECK
  // =============================================================================
  
  const { data: availability, isLoading: checkingAvailability } = useCheckFloorAvailability(
    buildingId,
    debouncedNumber || 0
  );
  
  // =============================================================================
  // MUTATIONS
  // =============================================================================
  
  const createMutation = useCreateFloor();
  
  /**
   * Handle form submission
   * Creates floor and handles errors
   */
  const handleSubmit = form.onSubmit(async (values) => {
    try {
      const result = await createMutation.mutateAsync({
        buildingId,
        number: parseInt(values.number),
        name: values.name.trim()
      });
      
      if (result.success) {
        onSuccess();
        form.reset();
        onClose();
      } else {
        // Handle specific error codes
        if (result.error?.code === 'FLOOR_NUMBER_EXISTS') {
          form.setFieldError('number', `Floor ${values.number} already exists`);
        } else {
          form.setErrors({ number: result.error?.message || 'Failed to create floor. Please try again.' });
        }
      }
    } catch (error: any) {
      form.setErrors({ number: 'Failed to create floor. Please try again.' });
    }
  });
  
  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================
  
  const canSubmit = form.isValid() && !checkingAvailability && (availability?.available ?? true);
  
  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconBuilding size={20} />
          <Text fw={600}>Add Floor to {buildingName}</Text>
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {/* Building Context Alert */}
          <Alert color="blue" icon={<IconBuilding />}>
            Adding floor to <strong>{buildingName}</strong>
          </Alert>
          
          {/* Floor Number Input */}
          <NumberInput
            label="Floor Number"
            placeholder="e.g., 1, 2, 3"
            required
            min={1}
            max={999}
            {...form.getInputProps('number')}
            rightSection={checkingAvailability && <Loader size="xs" />}
            description={
              availability && debouncedNumber ? (
                availability.available ? (
                  <Text size="sm" c="green">✓ Floor {debouncedNumber} is available</Text>
                ) : (
                  <Text size="sm" c="red">⚠️ Floor {debouncedNumber} already exists</Text>
                )
              ) : 'Enter a number between 1 and 999'
            }
          />
          
          {/* Suggestion Alert if unavailable */}
          {availability && !availability.available && debouncedNumber && (
            <Alert color="yellow" icon={<IconAlertCircle />}>
              Floor {debouncedNumber} already exists.
              <Group mt="xs">
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => form.setFieldValue('number', String(debouncedNumber + 1))}
                >
                  Try Floor {debouncedNumber + 1}
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => form.setFieldValue('number', String(debouncedNumber + 2))}
                >
                  Try Floor {debouncedNumber + 2}
                </Button>
              </Group>
            </Alert>
          )}
          
          {/* Floor Name Input */}
          <TextInput
            label="Floor Name"
            placeholder="e.g., Floor 1, Ground Floor"
            required
            {...form.getInputProps('name')}
            description="2-100 characters"
          />
          
          {/* Action Buttons */}
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={onClose} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending}
              disabled={!canSubmit}
              leftSection={<IconCheck size={16} />}
            >
              Create Floor
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

